import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import batteryCategoryServices from './batteryCategoryServices';
import type {
  ADD_BATTERY_CATEGORY,
  UPDATE_BATTERY_CATEGORY,
  BATTERY_CATEGORY,
} from './batteryCategory.type';
import type FILTERS from '../../../../common/filters.type';

const initialState = {
  isLoading: false,
  batteryCategories: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    is_active: undefined as undefined | boolean,
    totalDataCount: 0,
    pageData: [] as BATTERY_CATEGORY[],
  },
};

export const batteryCategoryList = createAsyncThunk(
  'batteryCategoryList',
  async (payload: FILTERS, thunkAPI) => {
    try {
      const data: AxiosResponse = await batteryCategoryServices.batteryCategoriesList(payload);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addBatteryCategory = createAsyncThunk(
  'addBatteryCategory',
  async (values: ADD_BATTERY_CATEGORY, thunkAPI) => {
    try {
      const data: AxiosResponse = await batteryCategoryServices.addBatteryCategory(values);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const updateBatteryCategory = createAsyncThunk(
  'updateBatteryCategory',
  async (
    values: {
      payload: UPDATE_BATTERY_CATEGORY;
      category_id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await batteryCategoryServices.updateBatteryCategory({
        payload: values.payload,
        category_id: values.category_id,
      });

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const batteryCategorySlice = createSlice({
  name: 'batteryCategories',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.batteryCategories.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.batteryCategories.page_number = 1;
      state.batteryCategories.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.batteryCategories.page_number = 1;
      state.batteryCategories.search = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.batteryCategories.ascending = action.payload.ascending;
      state.batteryCategories.sort_by = action.payload.sortBy;
    },
    clearBatteryCategoryFilters(state) {
      state.batteryCategories.page_size = 10;
      state.batteryCategories.page_number = 1;
      state.batteryCategories.search = '';
      state.batteryCategories.sort_by = 'name';
      state.batteryCategories.is_active = undefined;
    },
    batteryCategoryStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.batteryCategories.page_number = 1;
      state.batteryCategories.is_active = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(batteryCategoryList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(batteryCategoryList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.batteryCategories = action.payload.data;
    });
    builder.addCase(batteryCategoryList.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(addBatteryCategory.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addBatteryCategory.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Battery Category Added Successfully`);
    });
    builder.addCase(addBatteryCategory.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });

    builder.addCase(updateBatteryCategory.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateBatteryCategory.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Battery Category updated Successfully`);
    });
    builder.addCase(updateBatteryCategory.rejected, (state, action) => {
      state.isLoading = false;

      message.error(`${action.payload}`);
    });
  },
});
export const {
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  clearBatteryCategoryFilters,
  batteryCategoryStatusFilter,
} = batteryCategorySlice.actions;

export default batteryCategorySlice.reducer;
